import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  createArchitectSession,
  pingArchitectSession,
  streamArchitectMessage,
} from "../api/services/architectAgentService";
import {
  clearStoredAgentSession,
  getStoredAgentSession,
  setStoredAgentSession,
} from "../lib/agentSessionStorage";
import type { AgentContextUsage } from "../types/agentChat";

export interface ArchitectChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  /** Shorter text for the bubble when `content` is a long agent prompt. */
  displayContent?: string;
  timestamp: Date;
}

const WELCOME =
  "I'm the Architect Agent. Describe a new API to design, or select an existing API and ask me to enhance documentation, infer tags, or run a standards review.";

export function useArchitectAgentChat() {
  const { activeTenantId } = useAuth();
  const [messages, setMessages] = useState<ArchitectChatMessage[]>([
    {
      id: "welcome",
      role: "assistant",
      content: WELCOME,
      timestamp: new Date(),
    },
  ]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contextUsage, setContextUsage] = useState<AgentContextUsage | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const tenantRef = useRef(activeTenantId);

  useEffect(() => {
    if (tenantRef.current && tenantRef.current !== activeTenantId) {
      clearStoredAgentSession("architect", tenantRef.current);
      setSessionId(null);
      setSessionReady(false);
      setContextUsage(null);
    }
    tenantRef.current = activeTenantId;
  }, [activeTenantId]);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      if (!activeTenantId) {
        setSessionId(null);
        setSessionReady(true);
        return;
      }

      const stored = getStoredAgentSession("architect", activeTenantId);
      if (!stored?.sessionId) {
        setSessionId(null);
        setSessionReady(true);
        return;
      }

      try {
        const valid = await pingArchitectSession(stored.sessionId);
        if (cancelled) return;
        if (valid) {
          setSessionId(stored.sessionId);
        } else {
          clearStoredAgentSession("architect", activeTenantId);
          setSessionId(null);
        }
      } catch {
        if (!cancelled) {
          clearStoredAgentSession("architect", activeTenantId);
          setSessionId(null);
        }
      } finally {
        if (!cancelled) setSessionReady(true);
      }
    }

    setSessionReady(false);
    void restoreSession();
    return () => {
      cancelled = true;
    };
  }, [activeTenantId]);

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;

    const stored = getStoredAgentSession("architect", activeTenantId);
    if (stored?.sessionId) {
      const valid = await pingArchitectSession(stored.sessionId);
      if (valid) {
        setSessionId(stored.sessionId);
        return stored.sessionId;
      }
      clearStoredAgentSession("architect", activeTenantId);
    }

    const sid = await createArchitectSession();
    setSessionId(sid);
    if (activeTenantId) {
      setStoredAgentSession("architect", activeTenantId, sid);
    }
    return sid;
  }, [sessionId, activeTenantId]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || loading || !sessionReady) return;

      const userMessage: ArchitectChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: trimmed,
        timestamp: new Date(),
      };
      const assistantId = `assistant-${Date.now()}`;
      const assistantMessage: ArchitectChatMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setLoading(true);
      setError(null);

      try {
        const sid = await ensureSession();
        const result = await streamArchitectMessage(sid, trimmed, {
          onTextDelta: (delta) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, content: m.content + delta }
                  : m,
              ),
            );
          },
        });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: result.response }
              : m,
          ),
        );
        setContextUsage(result.context_usage ?? null);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Failed to send message");
        setMessages((prev) => {
          const assistant = prev.find((m) => m.id === assistantId);
          if (assistant && !assistant.content.trim()) {
            return prev.filter((m) => m.id !== assistantId);
          }
          return prev;
        });
      } finally {
        setLoading(false);
      }
    },
    [loading, sessionReady, ensureSession],
  );

  return { messages, sendMessage, loading, error, sessionId, sessionReady, contextUsage };
}
